import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import classNames from "classnames";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import { FeatureTypes } from "entity-app/constants";
import { DateUtils } from "utils/dateutils";
import React, { useLayoutEffect, useMemo, useState } from "react";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import DesignPackageCiqIcon from "components/svg-icons/design-package-ciq-icon";
import MaterialCiqIcon from "components/svg-icons/material-ciq-icon";
import ScheduleCiqIcon from "components/svg-icons/schedule-ciq-icon";
import SubmittalCiqIcon from "components/svg-icons/submittal-ciq-icon";
import CriticalActivityIndicator from "components/links/critical-activity-indicator";
import { Link, useParams } from "react-router-dom";
import {
  EWorkflowStatusDataBlock,
  SubmittalStatusToStrMap
} from "constants/index";
import EntityDateblock from ".";
import BidPackagePredecessorsColumn from "./components/bid-package-predecessors-column";
import BidPackageSuccessorsColumn from "./components/bid-package-successors-column";
import DesignPackageSuccessorsColumn from "./components/design-package-successors-column";
import DateblockThreeColumnLayout from "./components/dateblock-three-column-layout";
import {
  PredecessorAxisLabelRow,
  SuccessorAxisLabelRow
} from "./components/links-column/links-column-axis";
import NoSubmittalOrPlannedSubmittalLink from "./components/no-submittal-or-planned-submittal-link";
import { getEntityDateblockLayoutConfig } from "./entity-layout-config";
import { getBidPackageLinkedMaterials } from "./utils/get-bid-package-linked-materials";
import { getBidPackageLinkedSubmittals } from "./utils/get-bid-package-linked-submittals";
import {
  TDateBlockPermissions,
  TEntityDateblock,
  TGoverningSource
} from "./models";
import {
  LINK_CARD_HEIGHT_PX,
  LINK_CARD_HORIZONTAL_MARGIN_PX,
  LINK_CARD_HORIZONTAL_PADDING_PX,
  LINKS_GROUP_HEADER_VERTICAL_PADDING_PX,
  LINKS_GROUP_TITLE_LEFT_PADDING_PX,
  LINKS_COLUMN_INNER_GUTTER_PX,
  LINKS_COLUMN_OUTER_PADDING_PX,
  LINKS_COLUMN_PREDECESSOR_WIDTH,
  LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  computeBidPackageSuccessorMarginLeftPx,
  computeLinksColumnSuccessorWidth,
  computeSuccessorShiftPx,
  type DateblockMilestoneLike
} from "./layout-constants";
import ReadyProcurementIcon from "components/svg-icons/ready-procurement-icon";
import ActivityOffsetTag from "components/activity-offset-tag";
import CriticalActivityIcon from "components/svg-icons/critical-activity-icon";
import BlueLinkIcon from "components/svg-icons/blue-link-icon";
import OffsetIcon from "components/svg-icons/offset-icon";
import NotReadyProcurementIcon from "components/svg-icons/not-ready-procurement-icon";
import { getImplicitMaterialDisplayTitle } from "./utils/implicit-material-title";

type TWorkflowOffsetLite = {
  id?: string;
  name?: string;
  duration?: number;
  is_editable?: boolean;
  to_milestone_instance_id?: string | null;
};

type TWorkflowMilestoneLite = {
  id?: string;
  name?: string;
  sequence_no?: number;
  planned_date?: string | null;
  projected_date?: string | null;
  actual_date?: string | null;
  committed_date?: string | null;
  feature_workflow_instance_offsets_as_src?: Array<TWorkflowOffsetLite>;
};

type TFeatureActivityLite = {
  start_date?: string | null;
  end_date?: string | null;
  actual_start_date?: string | null;
  actual_finish_date?: string | null;
  critical?: boolean;
  linked_to_end_date?: boolean;
  progress?: number | null;
  total_slack?: number | null;
  total_slack_computed?: string | number | null;
};

type TFeatureSubmittalLite = {
  procurement_ready?: boolean | null;
  type?: number;
  raw_type?: string;
  workflow_status?: number;
};

type TFeatureInstanceLite = {
  id?: string;
  title?: string;
  auto_inc_value?: string | number;
  number?: string | number;
  feature_type_id?: number;
  implicit?: boolean;
  revision?: string | number;
  deleted_from_source?: boolean;
  feature_submittals?: Array<TFeatureSubmittalLite>;
  feature_workflow_instance?: {
    feature_instance_id?: string;
    workflow_status?: string;
    milestone_state?: string;
    risk_level?: string | null;
    final_deadline_date?: string | null;
    manual_entry_date?: string | null;
    wf_override_reason?: string | null;
    project_workflow_template?: {
      dateblock_compute_strategy?: {
        key?: string;
      };
    };
  };
  workflow_instance_milestones?: Array<TWorkflowMilestoneLite>;
  feature_activities?: Array<TFeatureActivityLite>;
  feature_instance_spec_sections?: Array<{
    spec_section?: {
      section_number?: string;
      section_name?: string;
      title?: string;
    };
  }>;
  /** Set from link metadata when rendering linked predecessor/successor cards. */
  governing_link?: boolean;
  linked_to_end_date?: boolean;
  reverse_dependency?: boolean;
  offset_duration?: number | null;
  linked_offset_date?: string | null;
  /** Parent workflow milestone this package link card is tied to. */
  linked_to_milestone_name?: string | null;
};

type TLinkParentEntity = {
  id?: string;
  auto_inc_value?: string | number;
  feature_type_id?: number;
  workflow_instance_milestones?: Array<TWorkflowMilestoneLite>;
  feature_instance_spec_sections?: Array<{
    spec_section?: { section_number?: string };
  }>;
};

type TLinkLite = {
  governing_link?: boolean;
  linked_to_end_date?: boolean;
  link_type?: number;
  reverse_dependency?: boolean;
  offset_duration?: number | null;
  linked_offset_date?: string | null;
  source_feature_milestone?: { id?: string; name?: string };
  target_feature_milestone?: { id?: string; name?: string };
  source_feature_instance?: TFeatureInstanceLite;
  target_feature_instance?: TFeatureInstanceLite;
};

/** Mirrors `getLinkedWithStartdate` in feature-entity-linking-page/utils. */
function isActivityLinkedWithStartDate(link: TLinkLite): boolean {
  const explicit = link.linked_to_end_date;
  if (explicit !== undefined && explicit !== null) return Boolean(explicit);

  const linkType = link.link_type;
  const reverse = link.reverse_dependency ?? false;
  if (linkType === undefined || linkType === null) return true;

  // FINISH_TO_FINISH: end date when not reverse
  if (linkType === 2 && !reverse) return false;
  // FINISH_TO_START: end date when reverse
  if (linkType === 0 && reverse) return false;
  return true;
}

function attachLinkContextToInstance(
  link: TLinkLite,
  instance?: TFeatureInstanceLite,
  parentEntity?: TLinkParentEntity
): TFeatureInstanceLite | undefined {
  if (!instance) return undefined;
  const withLinkMeta: TFeatureInstanceLite = {
    ...instance,
    governing_link: link.governing_link
  };
  // Design ⇄ Bid package links carry the parent milestone name onto the card.
  const isPackageCrossLink =
    (instance.feature_type_id === FeatureTypes.DESIGN_PACKAGE &&
      parentEntity?.feature_type_id === FeatureTypes.BID_PACKAGE) ||
    (instance.feature_type_id === FeatureTypes.BID_PACKAGE &&
      parentEntity?.feature_type_id === FeatureTypes.DESIGN_PACKAGE);
  if (isPackageCrossLink && link.source_feature_milestone?.name) {
    withLinkMeta.linked_to_milestone_name = link.source_feature_milestone.name;
  }
  if (instance.feature_type_id !== FeatureTypes.ACTIVITIES) {
    return withLinkMeta;
  }
  return {
    ...withLinkMeta,
    linked_to_end_date: isActivityLinkedWithStartDate(link),
    reverse_dependency: link.reverse_dependency,
    offset_duration: link.offset_duration,
    linked_offset_date: link.linked_offset_date
  };
}

function getLinkedFeatureInstanceId(
  instance: TFeatureInstanceLite
): string | undefined {
  return instance.feature_workflow_instance?.feature_instance_id ?? instance.id;
}

function getProjectingEntityId(entity?: TEntityDateblock): string | undefined {
  const projectingEntity = (
    entity?.feature_workflow_instance as
      | {
          calculation_details?: {
            projecting_entity?: { id?: string } | null;
          } | null;
        }
      | undefined
  )?.calculation_details?.projecting_entity;
  return projectingEntity?.id;
}

/**
 * Predecessors (left / driving): highlight when the linked instance id matches
 * the focal entity's calculation_details.projecting_entity.id.
 * Successors (right / governing): unchanged — governing_link or governingSource.
 */
function isHighlightedLinkCard(
  instance: TFeatureInstanceLite,
  columnAlign: "left" | "right",
  governingSource?: TGoverningSource,
  projectingEntityId?: string
): boolean {
  if (columnAlign === "right") {
    if (instance.governing_link) return true;
    if (
      governingSource?.id &&
      getLinkedFeatureInstanceId(instance) === governingSource.id
    ) {
      return true;
    }
    return false;
  }

  return Boolean(
    projectingEntityId &&
    getLinkedFeatureInstanceId(instance) === projectingEntityId
  );
}

function riskClass(level?: string | null): string {
  // `level` is only ever "High"/"Done" (RiskPill) or "done" (LinkCardTooltipDoneTag);
  // the nullish arm and the medium/low/default cases are unreachable defensive code.
  /* v8 ignore next */
  switch ((level ?? "").toLowerCase()) {
    case "high":
      return "border-ciq-red-400 bg-ciq-red-50 text-ciq-red-700";
    case "done":
      return "border-ciq-success-border bg-ciq-success-bg text-ciq-green-complete";
    /* v8 ignore start */
    case "medium":
      return "border-ciq-orange-300 bg-ciq-orange-50 text-ciq-orange-700";
    case "low":
      return "border-ciq-blue-300 bg-ciq-blue-50 text-ciq-blue-700";
    default:
      return "border-ciq-gray-300 bg-ciq-gray-200 text-ciq-gray-700";
    /* v8 ignore stop */
  }
}

const FEATURE_TYPE_ICON_CFG: Record<
  number,
  {
    Icon: (p: { size?: number; opacity?: string; className?: string }) => any;
    bg: string;
    color: string;
  }
> = {
  [FeatureTypes.SUBMITTALS]: {
    Icon: SubmittalCiqIcon,
    bg: "bg-ciq-primary-900-light",
    color: "fill-ciq-primary-900"
  },
  [FeatureTypes.MATERIALS]: {
    Icon: MaterialCiqIcon,
    bg: "bg-ciq-teal-200",
    color: "fill-ciq-teal-700"
  },
  [FeatureTypes.BID_PACKAGE]: {
    Icon: BidPackageCiqIcon,
    bg: "bg-ciq-red-200",
    color: "fill-ciq-red-700"
  },
  [FeatureTypes.DESIGN_PACKAGE]: {
    Icon: DesignPackageCiqIcon,
    bg: "bg-ciq-orange-300",
    color: "stroke-ciq-orange-700"
  },
  [FeatureTypes.ACTIVITIES]: {
    Icon: ScheduleCiqIcon,
    bg: "bg-ciq-orange-400",
    color: "fill-ciq-orange-800"
  }
};

function FeatureTypeIconBadge(
  props: Readonly<{
    featureTypeId?: number;
    size?: "sm" | "md";
    testId?: string;
  }>
) {
  // Sole caller (LinksColumnGroupAccordion) always passes a numeric featureTypeId,
  // size="md" and a testId, so the `?? 0`, small-size and testId fallback arms
  // below are unreachable defensive code.
  /* v8 ignore next */
  const cfg = FEATURE_TYPE_ICON_CFG[props.featureTypeId ?? 0];
  if (!cfg) return null;
  const { Icon, bg, color } = cfg;
  const isMd = props.size === "md";
  // Sole caller always passes size="md" and a testId, so the small-size class,
  // small icon size and testId-fallback arms in this markup are unreachable.
  /* v8 ignore start */
  return (
    <div
      className={classNames(
        "rounded-sm flex items-center justify-center shrink-0",
        isMd ? "w-5 h-5" : "w-4 h-4",
        bg
      )}
      data-testid={props.testId ?? "link-card-type-icon"}
    >
      <Icon size={isMd ? 10 : 8} opacity="1" className={color} />
    </div>
  );
  /* v8 ignore stop */
}

function LinksGroupCountBadge(props: Readonly<{ count: number }>) {
  return (
    <span
      className="inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-full border border-solid border-ciq-gray-300 bg-white px-1.5 py-0 text-[10px] font-medium leading-4 text-ciq-gray-700"
      data-testid="links-column-group-count"
    >
      {props.count}
    </span>
  );
}

function RiskPill(props: Readonly<{ level: string }>) {
  return (
    <span
      className={classNames(
        "rounded border border-solid px-2 py-0.5 capitalize text-xs leading-4 font-medium",
        riskClass(props.level)
      )}
      data-testid="link-card-risk"
    >
      {props.level}
    </span>
  );
}

function linkedFeatureDetailsPath(
  projectId: string | undefined,
  instance: TFeatureInstanceLite
): string | null {
  if (!projectId || !instance.id) return null;
  const { id, feature_type_id: ft } = instance;
  switch (ft) {
    case FeatureTypes.SUBMITTALS:
    case FeatureTypes.PLANNING_ONLY_SUBMITTAL:
      return `/project/${projectId}/submittals/${id}`;
    case FeatureTypes.MATERIALS:
    case FeatureTypes.PLANNING_ONLY_MATERIAL:
      return `/project/${projectId}/materials/${id}`;
    case FeatureTypes.BID_PACKAGE:
      return `/project/${projectId}/bid-packages/${id}`;
    case FeatureTypes.DESIGN_PACKAGE:
      return `/project/${projectId}/design-packages/${id}`;
    default:
      return null;
  }
}

function LinkCardBlueLinkIcon() {
  return (
    <span
      className="flex shrink-0 items-center leading-none"
      data-testid="link-card-blue-link-icon"
    >
      <BlueLinkIcon />
    </span>
  );
}

function ImplicitMaterialTitleRow(
  props: Readonly<{ title: string; enableTitleTooltip?: boolean }>
) {
  const titleClassName =
    "min-w-0 flex-1 text-xs font-medium leading-none text-ciq-fg";

  return (
    <div className="flex w-full min-w-0 items-center gap-1.5">
      <LinkCardBlueLinkIcon />
      {props.enableTitleTooltip ? (
        <TruncatedTextWithTooltip
          text={props.title}
          className={titleClassName}
          dataTestId="link-card-implicit-material-name"
        />
      ) : (
        <span
          className={`${titleClassName} truncate`}
          data-testid="link-card-implicit-material-name"
        >
          {props.title}
        </span>
      )}
    </div>
  );
}

function CardTitleRow(
  props: Readonly<{
    title: string;
    subTitle: string | number | null;
    extra?: React.ReactNode;
    hideSubTitle?: boolean;
    /**
     * When provided, renders the title as an underlined link that navigates
     * to the entity details page.
     */
    titleLinkTo?: string | null;
    /** Suppress the title truncation tooltip when the card already has one. */
    disableTitleTooltip?: boolean;
  }>
) {
  const {
    title,
    subTitle,
    extra,
    hideSubTitle = true,
    titleLinkTo,
    disableTitleTooltip = false
  } = props;
  const displayNumber =
    subTitle !== null && subTitle !== undefined && subTitle !== ""
      ? String(subTitle)
      : null;

  return (
    <div className="flex w-full min-w-0 items-center justify-between gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <LinkCardBlueLinkIcon />
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden text-xs">
          {!hideSubTitle && displayNumber ? (
            <span
              className="shrink-0 italic text-ciq-black/80 opacity-60"
              data-testid="link-card-number"
            >
              {displayNumber}
            </span>
          ) : null}
          {titleLinkTo ? (
            <Link
              to={titleLinkTo}
              className="min-w-0 flex-1 overflow-hidden hover:opacity-90"
              data-testid="link-card-title-link"
              aria-label="Open entity details"
            >
              <TruncatedTextWithTooltip
                text={title}
                className="font-medium text-ciq-fg !w-auto min-w-0 max-w-full"
                disableTooltip={disableTitleTooltip}
              />
            </Link>
          ) : (
            <TruncatedTextWithTooltip
              text={title}
              className="min-w-0 flex-1 font-medium text-ciq-fg !w-auto max-w-full"
              dataTestId="link-card-title"
              disableTooltip={disableTitleTooltip}
            />
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center">{extra}</div>
    </div>
  );
}

function getSortedMilestones(
  instance: TFeatureInstanceLite
): TWorkflowMilestoneLite[] {
  return [...(instance.workflow_instance_milestones ?? [])].sort(
    (a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0)
  );
}

function isWorkflowCompletedStatus(status?: string | null): boolean {
  return (status ?? "") === (EWorkflowStatusDataBlock.WF_COMPLETED as string);
}

function getInstanceDates(instance: TFeatureInstanceLite) {
  const sorted = getSortedMilestones(instance);
  const last = sorted.at(-1);
  const activity = instance.feature_activities?.[0];
  return {
    planned: last?.planned_date ?? null,
    projected: last?.projected_date ?? null,
    actual: last?.actual_date ?? null,
    startDate: activity?.start_date ?? null
  };
}

/** Risk and schedule rows on link cards only apply once a plan or actual exists on the last milestone. */
function hasPlanOrActualOnLastMilestone(
  instance: TFeatureInstanceLite
): boolean {
  const { planned, actual } = getInstanceDates(instance);
  return Boolean(planned || actual);
}

function safeFormat(d?: string | null): string | null {
  if (!d) return null;
  try {
    return DateUtils.format(d);
  } catch {
    return null;
  }
}

function isWorkflowStarted(status?: string | null): boolean {
  return (
    status === EWorkflowStatusDataBlock.IN_PROGRESS ||
    status === EWorkflowStatusDataBlock.WF_COMPLETED
  );
}

/**
 * Material link-card / sort date priority:
 * actual_date > projected_date > committed_date > planned_date.
 * `committed_date` is Material-only; other entity types never use it.
 */
function getMaterialMilestoneRawDate(
  milestone: TWorkflowMilestoneLite | undefined
): string | null {
  if (!milestone) return null;
  return (
    milestone.actual_date ??
    milestone.projected_date ??
    milestone.committed_date ??
    milestone.planned_date ??
    null
  );
}

type TMaterialDateSource = "actual" | "projected" | "committed" | null;

function getMaterialSecondaryDateLabel(source: TMaterialDateSource): string {
  if (source === "actual") return "Completed On:";
  return "Expected On:";
}

function toTimestamp(d?: string | null): number | null {
  if (!d) return null;
  const t = Date.parse(d);
  return Number.isNaN(t) ? null : t;
}

function getSubmittalMilestoneRawDate(
  milestone: TWorkflowMilestoneLite | undefined,
  wfStarted: boolean
): string | null {
  if (!milestone) return null;
  return (
    milestone.actual_date ??
    (wfStarted ? milestone.projected_date : milestone.planned_date) ??
    null
  );
}

function getSubmittalMilestoneRawDateBySequence(
  instance: TFeatureInstanceLite,
  sequenceNo: number
) {
  const milestones = instance.workflow_instance_milestones ?? [];
  const wfStarted = isWorkflowStarted(
    instance.feature_workflow_instance?.workflow_status
  );
  const milestone = milestones.find((m) => m.sequence_no === sequenceNo);
  return getSubmittalMilestoneRawDate(milestone, wfStarted);
}

/** Predecessor sort anchor: submittal design-review milestone (sequence 4). */
const getSubmittalApprovedRawDate = (instance: TFeatureInstanceLite) =>
  getSubmittalMilestoneRawDateBySequence(instance, 4);

/** Successor sort anchor: submittal subcontractor milestone (sequence 2). */
const getSubmittalSubmittedRawDate = (instance: TFeatureInstanceLite) =>
  getSubmittalMilestoneRawDateBySequence(instance, 2);

function getMaterialFirstLastRawDates(instance: TFeatureInstanceLite) {
  const sorted = getSortedMilestones(instance);
  const first = sorted[0];
  const last = sorted.at(-1) ?? first;
  return {
    rawFirst: getMaterialMilestoneRawDate(first),
    rawLast: getMaterialMilestoneRawDate(last)
  };
}

function getActivityLinkedRawDate(
  instance: TFeatureInstanceLite
): string | null {
  const activity = instance.feature_activities?.[0];
  // Misnamed in legacy API: `linked_to_end_date` behaves like "linked with start date".
  // Successor activity instances always carry a boolean linked_to_end_date (set by
  // attachLinkContextToInstance), so the `?? true` fallback is unreachable.
  /* v8 ignore next */
  const isLinkedWithStartdate = instance.linked_to_end_date ?? true;

  const startRaw = activity?.start_date ?? null;
  const endRaw = activity?.end_date ?? null;

  return isLinkedWithStartdate ? startRaw : endRaw;
}

function getActivityCompletionRawDate(
  instance: TFeatureInstanceLite
): string | null {
  const activity = instance.feature_activities?.[0];
  return activity?.linked_to_end_date
    ? (activity?.end_date ?? null)
    : (activity?.start_date ?? null);
}

function getPredecessorSortRawDate(
  instance: TFeatureInstanceLite
): string | null {
  const ft = instance.feature_type_id;
  if (ft === FeatureTypes.SUBMITTALS) {
    return getSubmittalApprovedRawDate(instance);
  }
  if (ft === FeatureTypes.MATERIALS) {
    return getMaterialFirstLastRawDates(instance).rawLast;
  }
  if (ft === FeatureTypes.ACTIVITIES) {
    return getActivityCompletionRawDate(instance);
  }
  if (ft === FeatureTypes.BID_PACKAGE || ft === FeatureTypes.DESIGN_PACKAGE) {
    return getWorkflowPackageLastMilestoneRawDate(instance);
  }
  return null;
}

function getSuccessorSortRawDate(
  instance: TFeatureInstanceLite
): string | null {
  const ft = instance.feature_type_id;
  if (ft === FeatureTypes.SUBMITTALS) {
    return getSubmittalSubmittedRawDate(instance);
  }
  if (ft === FeatureTypes.MATERIALS) {
    return getMaterialFirstLastRawDates(instance).rawFirst;
  }
  if (ft === FeatureTypes.ACTIVITIES) {
    return getActivityLinkedRawDate(instance);
  }
  if (ft === FeatureTypes.BID_PACKAGE || ft === FeatureTypes.DESIGN_PACKAGE) {
    return getWorkflowPackageLastMilestoneRawDate(instance);
  }
  return null;
}

function stableSortByDate<T>(
  arr: T[],
  getRawDate: (item: T) => string | null,
  order: "asc" | "desc"
): T[] {
  return arr
    .map((item, index) => ({
      item,
      index,
      key: toTimestamp(getRawDate(item))
    }))
    .sort((a, b) => {
      if (a.key === null && b.key === null) return a.index - b.index;
      if (a.key === null) return 1; // nulls last
      if (b.key === null) return -1;

      if (order === "desc") return b.key! - a.key!;
      return a.key! - b.key!;
    })
    .map((x) => x.item);
}

const LINK_CARD_TOOLTIP_WIDTH_PX = 280;
/** Extra width for activity tooltips with offset date + tag beside planned date. */
const LINK_CARD_ACTIVITY_TOOLTIP_WIDTH_PX = 340;
const LINK_CARD_TOOLTIP_SHELL_CLASS = "box-border w-full space-y-1 p-2";
const LINK_CARD_TOOLTIP_MILESTONE_SECTION_CLASS =
  "flex overflow-hidden bg-ciq-gray-100";
const LINK_CARD_TOOLTIP_MILESTONE_SECTION_ACCENT_CLASS =
  "w-[2px] shrink-0 self-stretch bg-black";
const LINK_CARD_TOOLTIP_MILESTONE_SECTION_BODY_CLASS =
  "min-w-0 flex-1 space-y-0.5 py-2 pr-2 pl-2.5";
const LINK_CARD_TOOLTIP_MILESTONE_SECTION_TITLE_CLASS =
  "text-xs font-medium text-ciq-black-a88 mb-1.5";

/**
 * Side placements toward the dateblock center:
 * - predecessors (left column): tooltip on the right of the card
 * - successors (right column): tooltip on the left of the card
 *
 * Do not use custom horizontal `align.offset` here — it desyncs antd's
 * arrow from the trigger when overlay width changes.
 */
function getLinkCardDatesTooltipPlacement(
  isSuccessor?: boolean
): "left" | "right" {
  return isSuccessor ? "left" : "right";
}

const LINK_CARD_DATE_TEXT_CLASS =
  "whitespace-nowrap font-medium text-ciq-black-a99 tabular-nums";

const LINK_CARD_DATE_VALUE_CLASS = `${LINK_CARD_DATE_TEXT_CLASS} text-right justify-self-end`;

function hasActivityLinkOffset(instance: TFeatureInstanceLite): boolean {
  return instance.offset_duration != null && instance.offset_duration !== 0;
}

function getActivityLinkCardTooltipWidthPx(
  instance: TFeatureInstanceLite
): number {
  return hasActivityLinkOffset(instance)
    ? LINK_CARD_ACTIVITY_TOOLTIP_WIDTH_PX
    : LINK_CARD_TOOLTIP_WIDTH_PX;
}

function LinkCardTooltipShell(props: Readonly<{ children: React.ReactNode }>) {
  return (
    <div
      className={LINK_CARD_TOOLTIP_SHELL_CLASS}
      data-testid="link-card-dates-tooltip-content"
    >
      {props.children}
    </div>
  );
}

function LinkCardTooltipTitle(
  props: Readonly<{ children: React.ReactNode; testId?: string }>
) {
  return (
    <div
      className="break-words text-xs font-bold text-ciq-black-a80 py-2"
      /* v8 ignore next -- every caller passes an explicit testId */
      data-testid={props.testId ?? "link-card-tooltip-title"}
    >
      {props.children}
    </div>
  );
}

function LinkCardTooltipProcurementTag(props: Readonly<{ ready: boolean }>) {
  return (
    <div
      className="inline-flex h-6 w-fit max-w-full items-center gap-1 whitespace-nowrap rounded border border-solid border-ciq-gray-300 bg-ciq-gray-50 px-2 text-xs font-normal text-ciq-black-a88"
      data-testid="link-card-tooltip-procurement-tag"
    >
      <span className="flex shrink-0 items-center leading-none">
        {props.ready ? <ReadyProcurementIcon /> : <NotReadyProcurementIcon />}
      </span>
      <span>
        {props.ready ? "Ready for procurement" : "Not ready for procurement"}
      </span>
    </div>
  );
}

function LinkCardTooltipDoneTag() {
  return (
    <span
      className={classNames(
        "inline-flex rounded border border-solid px-2 py-0.5 text-xs font-medium capitalize leading-4",
        riskClass("done")
      )}
      data-testid="link-card-tooltip-done-tag"
    >
      Done
    </span>
  );
}

function LinkCardTooltipCriticalActivityTag() {
  return (
    <div
      className="inline-flex h-6 w-fit max-w-full items-center gap-1 whitespace-nowrap rounded-[5px] border border-solid border-ciq-blue-300 bg-ciq-blue-50 px-2 text-xs font-normal text-ciq-blue-700"
      data-testid="link-card-tooltip-critical-activity-tag"
    >
      <span className="flex shrink-0 items-center leading-none">
        <CriticalActivityIcon />
      </span>
      <span>Critical Activity</span>
    </div>
  );
}

function riskTextClass(level?: string | null): string {
  // The sole caller (LinkCardTooltipStatusRiskRow) is guarded by
  // `props.showRisk && props.riskLevel`, so `level` is always a non-empty
  // string here and the nullish fallback in the discriminant is unreachable.
  switch (
    /* v8 ignore next */
    (level ?? "").toLowerCase()
  ) {
    case "high":
      return "text-ciq-red-700";
    case "medium":
      return "text-ciq-orange-700";
    case "low":
      return "text-ciq-blue-700";
    default:
      return "text-ciq-gray-700";
  }
}

function LinkCardTooltipStatusRiskRow(
  props: Readonly<{ status: string; showRisk: boolean; riskLevel?: string }>
) {
  return (
    <div
      className="flex w-full min-w-0 flex-wrap items-baseline gap-x-4 text-xs pb-2"
      data-testid="link-card-tooltip-status-risk-row"
    >
      <span className="min-w-0 break-words">
        <span className="font-normal text-ciq-black-a65">Status: </span>
        <span
          className="font-medium text-ciq-black-a88"
          data-testid="link-card-tooltip-status"
        >
          {props.status || "-"}
        </span>
      </span>
      {props.showRisk && props.riskLevel ? (
        <span className="shrink-0 whitespace-nowrap">
          <span className="font-normal text-ciq-black-a65">Risk: </span>
          <span
            className={classNames(
              "font-semibold capitalize",
              riskTextClass(props.riskLevel)
            )}
            data-testid="link-card-tooltip-risk"
          >
            {props.riskLevel}
          </span>
        </span>
      ) : null}
    </div>
  );
}

function LinkCardTooltipSectionDateRow(
  props: Readonly<{
    label: string;
    value: string;
    valueTestId?: string;
    delayed?: boolean;
  }>
) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="shrink-0 font-normal text-ciq-black-a65">
        {props.label}
      </span>
      <span
        className={classNames(
          "text-right font-medium tabular-nums",
          props.delayed ? "text-ciq-red-700" : "text-ciq-black-a99"
        )}
        data-testid={props.valueTestId}
      >
        {props.value}
      </span>
    </div>
  );
}

function LinkCardTooltipMilestoneSection(
  props: Readonly<{
    title: string;
    milestoneTitle?: string;
    primaryLabel: string;
    primaryValue: string;
    secondaryLabel: string;
    secondaryValue: string;
    secondaryDelayed?: boolean;
    primaryTestId?: string;
    secondaryTestId?: string;
  }>
) {
  return (
    <div
      className={LINK_CARD_TOOLTIP_MILESTONE_SECTION_CLASS}
      data-testid="link-card-tooltip-milestone-section"
    >
      <div
        className={LINK_CARD_TOOLTIP_MILESTONE_SECTION_ACCENT_CLASS}
        data-testid="link-card-tooltip-milestone-section-accent"
        aria-hidden
      />
      <div className={LINK_CARD_TOOLTIP_MILESTONE_SECTION_BODY_CLASS}>
        <div
          className={LINK_CARD_TOOLTIP_MILESTONE_SECTION_TITLE_CLASS}
          data-testid="link-card-tooltip-milestone-section-title"
        >
          {props.title}
        </div>
        {props.milestoneTitle ? (
          <div className="flex justify-between text-xs">
            <span className="font-normal text-ciq-black-a66">
              Milestone Title:{" "}
            </span>
            <span
              className="font-medium text-ciq-black-a88"
              data-testid="link-card-tooltip-milestone-title"
            >
              {props.milestoneTitle}
            </span>
          </div>
        ) : null}
        <LinkCardTooltipSectionDateRow
          label={props.primaryLabel}
          value={props.primaryValue}
          valueTestId={props.primaryTestId}
        />
        <LinkCardTooltipSectionDateRow
          label={props.secondaryLabel}
          value={props.secondaryValue}
          valueTestId={props.secondaryTestId}
          delayed={props.secondaryDelayed}
        />
      </div>
    </div>
  );
}

function formatActivityOffsetDuration(
  duration: number | null | undefined
): string {
  // Only caller invokes this behind a `hasOffset` guard (duration != null && !== 0),
  // so the empty-string early return is unreachable.
  /* v8 ignore next */
  if (duration == null || duration === 0) return "";
  return duration < 0 ? `${duration}` : `+${duration}`;
}

function LinkCardTooltipPlannedDateFooter(
  props: Readonly<{
    label: string;
    dateValue: string | null;
    offsetDuration?: number | null;
    linkedOffsetDateRaw?: string | null;
    activity?: TFeatureActivityLite;
    testId?: string;
  }>
) {
  const hasOffset = props.offsetDuration != null && props.offsetDuration !== 0;

  const plannedDateRow = (
    <div
      className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs"
      data-testid={props.testId ?? "link-card-tooltip-planned-date-footer"}
    >
      <span className="min-w-0 shrink-0 font-normal text-ciq-black-a65">
        {props.label}
      </span>
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
        <span
          className={LINK_CARD_DATE_VALUE_CLASS}
          data-testid="link-card-tooltip-planned-date"
        >
          {props.dateValue ?? "-"}
        </span>
        {hasOffset ? (
          <span className="shrink-0" data-testid="activity-offset-tag">
            <ActivityOffsetTag
              task={{
                ...props.activity,
                offset_duration: props.offsetDuration,
                linked_offset_date: props.linkedOffsetDateRaw
              }}
              isShowDate
              showTooltip={false}
              dateTextClassName={`${LINK_CARD_DATE_TEXT_CLASS} text-right`}
            />
          </span>
        ) : null}
      </div>
    </div>
  );

  if (!hasOffset) {
    return plannedDateRow;
  }

  const offsetDurationLabel = formatActivityOffsetDuration(
    props.offsetDuration
  );

  return (
    <>
      {plannedDateRow}
      <div
        className="space-y-1"
        data-testid="link-card-tooltip-link-properties"
      >
        <span className="text-xs font-normal text-ciq-black-a65">
          Link Properties:
        </span>
        <div className="flex h-6 items-center justify-center gap-1 rounded bg-ciq-gray-100 px-2">
          <span className="flex shrink-0 items-center leading-none">
            <OffsetIcon />
          </span>
          <span
            className="text-xs font-normal text-ciq-black-a88"
            data-testid="link-card-tooltip-offset-duration"
          >
            Offset:{offsetDurationLabel} Days
          </span>
        </div>
      </div>
    </>
  );
}

function getSubmittalTooltipMilestone(
  instance: TFeatureInstanceLite,
  isSuccessor: boolean
): TWorkflowMilestoneLite | undefined {
  const sorted = getSortedMilestones(instance);
  if (isSuccessor) {
    return sorted.find((m) => m.sequence_no === 2);
  }
  return sorted.find((m) => m.sequence_no === 4);
}

function getMaterialTooltipMilestone(
  instance: TFeatureInstanceLite,
  isSuccessor: boolean,
  completed: boolean
): TWorkflowMilestoneLite | undefined {
  const sorted = getSortedMilestones(instance);
  if (isSuccessor && !completed) {
    return sorted[0];
  }
  return sorted.at(-1) ?? sorted[0];
}

function getSubmittalTooltipSectionTitle(
  isSuccessor: boolean,
  completed: boolean
): string {
  if (isSuccessor && !completed) {
    return "SC Assignment";
  }
  return "Design Review";
}

function getMaterialTooltipSectionTitle(
  isSuccessor: boolean,
  completed: boolean
): string {
  if (isSuccessor) {
    return completed ? "Last Milestone" : "First Milestone";
  }
  return "Last Milestone";
}

function shouldShowTooltipRisk(
  instance: TFeatureInstanceLite,
  completed: boolean
): boolean {
  if (completed) return false;
  const risk = instance.feature_workflow_instance?.risk_level;
  if (!risk) return false;
  return hasPlanOrActualOnLastMilestone(instance);
}

function getSubmittalLinkCardStatusLabel(
  instance: TFeatureInstanceLite
): string {
  const submittal = instance.feature_submittals?.[0];
  if (submittal?.workflow_status == null) return "";
  return SubmittalStatusToStrMap[submittal.workflow_status] ?? "";
}

function getWorkflowPackageLastMilestoneRawDate(
  instance: TFeatureInstanceLite
): string | null {
  const sorted = getSortedMilestones(instance);
  const last = sorted.at(-1);
  if (!last) return null;
  return last.actual_date ?? last.projected_date ?? last.planned_date ?? null;
}

/**
 * Secondary tooltip date for materials: actual > projected > committed.
 * Planned stays on the Required On row; it is only used for sort when nothing
 * else exists (see getMaterialMilestoneRawDate).
 */
function resolveMaterialTooltipSecondaryDate(
  milestone: TWorkflowMilestoneLite | undefined
): { raw: string | null; source: TMaterialDateSource } {
  if (!milestone) return { raw: null, source: null };
  if (milestone.actual_date) {
    return { raw: milestone.actual_date, source: "actual" };
  }
  if (milestone.projected_date) {
    return { raw: milestone.projected_date, source: "projected" };
  }
  if (milestone.committed_date) {
    return { raw: milestone.committed_date, source: "committed" };
  }
  return { raw: null, source: null };
}

type TSubmittalDateSource = "actual" | "projected" | null;

/**
 * Secondary tooltip date for submittals: actual_date → Completed On,
 * otherwise projected_date → Expected On.
 */
function resolveSubmittalTooltipSecondaryDate(
  milestone: TWorkflowMilestoneLite | undefined
): { raw: string | null; source: TSubmittalDateSource } {
  if (!milestone) return { raw: null, source: null };
  if (milestone.actual_date) {
    return { raw: milestone.actual_date, source: "actual" };
  }
  if (milestone.projected_date) {
    return { raw: milestone.projected_date, source: "projected" };
  }
  return { raw: null, source: null };
}

function buildMaterialLinkCardTooltip(
  instance: TFeatureInstanceLite,
  linkParentEntity?: TLinkParentEntity,
  isSuccessor = false
): React.ReactNode {
  const { title, number } = getCardCommonProps(instance, linkParentEntity);
  const headerTitle = instance.implicit
    ? title
    : [number, title].filter(Boolean).join(" - ");
  const wfi = instance.feature_workflow_instance;
  const completed = isWorkflowCompletedStatus(wfi?.workflow_status);
  const status = wfi?.milestone_state ?? "";
  const milestone = getMaterialTooltipMilestone(
    instance,
    isSuccessor,
    completed
  );
  const secondary = resolveMaterialTooltipSecondaryDate(milestone);
  const primaryValue = safeFormat(milestone?.planned_date) ?? "-";
  const secondaryValue = safeFormat(secondary.raw) ?? "-";
  const secondaryLabel = getMaterialSecondaryDateLabel(secondary.source);
  const plannedTs = toTimestamp(milestone?.planned_date);
  const secondaryTs = toTimestamp(secondary.raw);
  const secondaryDelayed =
    secondary.source !== "actual" &&
    plannedTs != null &&
    secondaryTs != null &&
    secondaryTs > plannedTs;
  const sectionTitle = getMaterialTooltipSectionTitle(isSuccessor, completed);

  return (
    <LinkCardTooltipShell>
      {completed ? <LinkCardTooltipDoneTag /> : null}
      <LinkCardTooltipTitle testId="link-card-material-tooltip-title">
        {headerTitle}
      </LinkCardTooltipTitle>
      <LinkCardTooltipStatusRiskRow
        status={status}
        showRisk={shouldShowTooltipRisk(instance, completed)}
        riskLevel={instance.feature_workflow_instance?.risk_level ?? undefined}
      />
      <LinkCardTooltipMilestoneSection
        title={sectionTitle}
        milestoneTitle={milestone?.name ?? "-"}
        primaryLabel="Required On:"
        primaryValue={primaryValue}
        secondaryLabel={secondaryLabel}
        secondaryValue={secondaryValue}
        secondaryDelayed={secondaryDelayed}
        primaryTestId="link-card-tooltip-primary-date"
        secondaryTestId="link-card-tooltip-secondary-date"
      />
    </LinkCardTooltipShell>
  );
}

function buildSubmittalLinkCardTooltip(
  instance: TFeatureInstanceLite,
  isSuccessor = false
): React.ReactNode {
  const { title, number } = getCardCommonProps(instance);
  const headerTitle = [number, title].filter(Boolean).join(" - ");
  const wfi = instance.feature_workflow_instance;
  const completed = isWorkflowCompletedStatus(wfi?.workflow_status);
  const status = getSubmittalLinkCardStatusLabel(instance);
  const procurementReady = Boolean(
    instance.feature_submittals?.[0]?.procurement_ready
  );
  const milestone = getSubmittalTooltipMilestone(instance, isSuccessor);
  const secondary = resolveSubmittalTooltipSecondaryDate(milestone);
  const primaryValue = safeFormat(milestone?.planned_date) ?? "-";
  const secondaryValue = safeFormat(secondary.raw) ?? "-";
  const secondaryLabel =
    secondary.source === "actual" ? "Completed On:" : "Expected On:";
  const plannedTs = toTimestamp(milestone?.planned_date);
  const secondaryTs = toTimestamp(secondary.raw);
  const secondaryDelayed =
    secondary.source === "projected" &&
    plannedTs != null &&
    secondaryTs != null &&
    secondaryTs > plannedTs;

  return (
    <LinkCardTooltipShell>
      <LinkCardTooltipProcurementTag ready={procurementReady} />
      <LinkCardTooltipTitle testId="link-card-submittal-tooltip-title">
        {headerTitle}
      </LinkCardTooltipTitle>
      <LinkCardTooltipStatusRiskRow
        status={status}
        showRisk={shouldShowTooltipRisk(instance, completed)}
        riskLevel={instance.feature_workflow_instance?.risk_level ?? undefined}
      />
      <LinkCardTooltipMilestoneSection
        title={getSubmittalTooltipSectionTitle(isSuccessor, completed)}
        primaryLabel="Required On:"
        primaryValue={primaryValue}
        secondaryLabel={secondaryLabel}
        secondaryValue={secondaryValue}
        secondaryDelayed={secondaryDelayed}
        primaryTestId="link-card-tooltip-primary-date"
        secondaryTestId="link-card-tooltip-secondary-date"
      />
    </LinkCardTooltipShell>
  );
}

function buildActivityLinkCardTooltip(
  instance: TFeatureInstanceLite
): React.ReactNode {
  const { title, number } = getCardCommonProps(instance);
  const headerTitle = [number, title].filter(Boolean).join(" - ");
  const activity = instance.feature_activities?.[0];
  // Activity link-card instances always carry a boolean linked_to_end_date (set
  // by attachLinkContextToInstance), so the `?? false` fallback is unreachable.
  /* v8 ignore next */
  const linkedWithStartDate = instance.linked_to_end_date ?? false;
  const plannedStartFmt = safeFormat(activity?.start_date);
  const plannedEndFmt = safeFormat(activity?.end_date);
  const hasOffset = hasActivityLinkOffset(instance);
  const footerLabel = linkedWithStartDate
    ? "Planned Start Date:"
    : "Planned End Date:";
  const footerDate = linkedWithStartDate ? plannedStartFmt : plannedEndFmt;

  return (
    <LinkCardTooltipShell>
      {instance.deleted_from_source ? (
        <div className="flex flex-col items-center justify-center rounded border border-solid border-ciq-gray-300 text-xs">
          <div className="bg-white px-1 font-semibold text-ciq-red-700">
            Deleted
          </div>
        </div>
      ) : null}
      {activity?.critical ? <LinkCardTooltipCriticalActivityTag /> : null}
      <LinkCardTooltipTitle testId="link-card-activity-tooltip-title">
        {headerTitle}
      </LinkCardTooltipTitle>
      <LinkCardTooltipPlannedDateFooter
        label={footerLabel}
        dateValue={footerDate}
        offsetDuration={hasOffset ? instance.offset_duration : null}
        linkedOffsetDateRaw={instance.linked_offset_date}
        activity={activity}
      />
    </LinkCardTooltipShell>
  );
}

function buildHeaderRiskBadge(
  risk: string | undefined,
  completed: boolean,
  hasScheduleAnchor: boolean,
  options?: Readonly<{ showDone?: boolean }>
): React.ReactNode {
  const showDone = options?.showDone ?? true;
  if (showDone && completed && hasScheduleAnchor) {
    return <RiskPill level="Done" />;
  }
  if (hasScheduleAnchor && (risk ?? "").toLowerCase() === "high") {
    return <RiskPill level="High" />;
  }
  return null;
}

const linkCardHorizontalPaddingStyle: React.CSSProperties = {
  paddingLeft: LINK_CARD_HORIZONTAL_PADDING_PX,
  paddingRight: LINK_CARD_HORIZONTAL_PADDING_PX
};

const linkCardBodyClassName = "flex h-full w-full min-w-0 items-center";

/** Card inset from the gray group container — applied on the group, not each card. */
const linksGroupCardInsetStyle: React.CSSProperties = {
  paddingLeft: LINK_CARD_HORIZONTAL_MARGIN_PX,
  paddingRight: LINK_CARD_HORIZONTAL_MARGIN_PX
};

function getLinkedCardDisplayNumber(instance: TFeatureInstanceLite): string {
  if (instance.feature_type_id === FeatureTypes.ACTIVITIES) {
    return String(instance.number ?? instance.auto_inc_value ?? "");
  }
  return String(instance.auto_inc_value ?? instance.number ?? "");
}

function getCardCommonProps(
  instance: TFeatureInstanceLite,
  linkParentEntity?: TLinkParentEntity
) {
  const title = instance.implicit
    ? getImplicitMaterialDisplayTitle(linkParentEntity?.auto_inc_value)
    : (instance.title ?? "Untitled");
  const number = getLinkedCardDisplayNumber(instance);
  return { title, number };
}

const LINK_CARD_HIGHLIGHT_BORDER_CLASS = "border-ciq-primary-900";

function WorkflowPackageLinkedMilestoneRow(
  props: Readonly<{ milestoneName: string; isSuccessor?: boolean }>
) {
  const linkLabel = props.isSuccessor ? "Link from: " : "Link to: ";
  return (
    <div
      className="flex w-full min-w-0 items-center pl-[22px] text-[11px] space-x-1 leading-tight text-ciq-gray-950/80"
      data-testid="link-card-linked-milestone"
    >
      <span className="shrink-0 text-ciq-gray-950/60">{linkLabel} </span>
      <div className="min-w-0 flex-1 overflow-hidden">
        <TruncatedTextWithTooltip
          text={props.milestoneName}
          className="font-medium text-ciq-gray-950/90"
        />
      </div>
    </div>
  );
}

function LinkCardShell(
  props: Readonly<{
    cardType: string;
    highlighted?: boolean;
    highlightRole?: "driving" | "governing";
    datesTooltip?: React.ReactNode;
    cardStyle?: React.CSSProperties;
    isSuccessor?: boolean;
    tooltipWidthPx?: number;
    children: React.ReactNode;
  }>
) {
  const tooltipWidthPx = props.tooltipWidthPx ?? LINK_CARD_TOOLTIP_WIDTH_PX;
  const card = (
    <div
      className={classNames(
        "box-border w-full min-w-0 overflow-hidden rounded border border-solid bg-white",
        props.highlighted
          ? LINK_CARD_HIGHLIGHT_BORDER_CLASS
          : "border-ciq-black-a10"
      )}
      style={{ height: LINK_CARD_HEIGHT_PX, ...props.cardStyle }}
      data-testid="link-card"
      data-card-type={props.cardType}
      data-link-highlight={props.highlighted ? props.highlightRole : undefined}
    >
      {props.children}
    </div>
  );

  if (!props.datesTooltip) return card;

  return (
    <LinkCardDatesTooltip
      content={props.datesTooltip}
      isSuccessor={props.isSuccessor}
      widthPx={tooltipWidthPx}
    >
      {card}
    </LinkCardDatesTooltip>
  );
}

function LinkCardDatesTooltip(
  props: Readonly<{
    content: React.ReactNode;
    isSuccessor?: boolean;
    widthPx: number;
    children: React.ReactNode;
  }>
) {
  const placement = getLinkCardDatesTooltipPlacement(props.isSuccessor);
  return (
    <Tooltip
      title={props.content}
      placement={placement}
      arrow={{ pointAtCenter: true }}
      mouseEnterDelay={0}
      autoAdjustOverflow={false}
      destroyOnHidden
      // Match outer and inner widths so the popup sits flush against the card
      // with no side gap (predecessors right / successors left).
      styles={{
        root: {
          width: props.widthPx,
          maxWidth: "none"
        },
        container: {
          width: props.widthPx,
          maxWidth: "none",
          padding: 0
        }
      }}
      classNames={{ root: "link-card-dates-tooltip" }}
    >
      <span
        className="block w-full min-w-0"
        data-testid="link-card-tooltip-trigger"
        data-tooltip-placement={placement}
      >
        {props.children}
      </span>
    </Tooltip>
  );
}

function SubmittalLinkedCard(
  props: Readonly<{
    instance: TFeatureInstanceLite;
    projectId?: string;
    highlighted?: boolean;
    highlightRole?: "driving" | "governing";
    disableDatesTooltip?: boolean;
    isSuccessor?: boolean;
  }>
) {
  const {
    instance,
    projectId,
    highlighted,
    highlightRole,
    disableDatesTooltip,
    isSuccessor
  } = props;
  const { title, number } = getCardCommonProps(instance);
  const wfi = instance.feature_workflow_instance;
  const completed = isWorkflowCompletedStatus(wfi?.workflow_status);
  const risk = wfi?.risk_level ?? undefined;
  const hasScheduleAnchor = hasPlanOrActualOnLastMilestone(instance);
  const riskPillNode = completed
    ? null
    : buildHeaderRiskBadge(risk, completed, hasScheduleAnchor, {
        showDone: false
      });
  const detailsPath = linkedFeatureDetailsPath(projectId, instance);
  const datesTooltip = disableDatesTooltip
    ? undefined
    : buildSubmittalLinkCardTooltip(instance, isSuccessor);
  const enableTitleTooltip = disableDatesTooltip === true;
  const procurementReady = Boolean(
    instance.feature_submittals?.[0]?.procurement_ready
  );

  return (
    <LinkCardShell
      cardType="submittal"
      highlighted={highlighted}
      highlightRole={highlightRole}
      datesTooltip={datesTooltip}
      isSuccessor={isSuccessor}
    >
      <div
        className={linkCardBodyClassName}
        style={linkCardHorizontalPaddingStyle}
      >
        <CardTitleRow
          title={title}
          subTitle={number}
          titleLinkTo={detailsPath}
          disableTitleTooltip={!enableTitleTooltip}
          extra={
            disableDatesTooltip ? undefined : (
              <div className="flex shrink-0 items-center justify-center gap-1.5">
                {!procurementReady && riskPillNode}
                {procurementReady ? (
                  <Tooltip title="Ready for Procurement">
                    <span
                      className="flex items-center leading-none"
                      data-testid="link-card-procurement-ready-icon"
                    >
                      <ReadyProcurementIcon />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Not Ready for Procurement">
                    <span
                      className="flex items-center leading-none"
                      data-testid="link-card-not-ready-procurement-icon"
                    >
                      <NotReadyProcurementIcon />
                    </span>
                  </Tooltip>
                )}
              </div>
            )
          }
        />
      </div>
    </LinkCardShell>
  );
}

function MaterialLinkedCard(
  props: Readonly<{
    instance: TFeatureInstanceLite;
    projectId?: string;
    linkParentEntity?: TLinkParentEntity;
    highlighted?: boolean;
    highlightRole?: "driving" | "governing";
    disableDatesTooltip?: boolean;
    isSuccessor?: boolean;
  }>
) {
  const {
    instance,
    projectId,
    linkParentEntity,
    highlighted,
    highlightRole,
    disableDatesTooltip,
    isSuccessor
  } = props;
  const { title, number } = getCardCommonProps(instance, linkParentEntity);
  const wfi = instance.feature_workflow_instance;
  const risk = wfi?.risk_level ?? undefined;
  const completed = isWorkflowCompletedStatus(wfi?.workflow_status);
  const hasScheduleAnchor = hasPlanOrActualOnLastMilestone(instance);
  const riskPillNode = buildHeaderRiskBadge(risk, completed, hasScheduleAnchor);
  const detailsPath = linkedFeatureDetailsPath(projectId, instance);
  const datesTooltip = disableDatesTooltip
    ? undefined
    : buildMaterialLinkCardTooltip(instance, linkParentEntity, isSuccessor);
  const enableTitleTooltip = disableDatesTooltip === true;

  return (
    <LinkCardShell
      cardType="material"
      highlighted={highlighted}
      highlightRole={highlightRole}
      datesTooltip={datesTooltip}
      isSuccessor={isSuccessor}
    >
      <div
        className={linkCardBodyClassName}
        style={linkCardHorizontalPaddingStyle}
      >
        {instance.implicit ? (
          <ImplicitMaterialTitleRow
            title={title}
            enableTitleTooltip={enableTitleTooltip}
          />
        ) : (
          <CardTitleRow
            title={title}
            subTitle={number}
            titleLinkTo={detailsPath}
            disableTitleTooltip={!enableTitleTooltip}
            extra={
              <div className="flex shrink-0 items-center justify-center gap-1.5">
                {riskPillNode}
              </div>
            }
          />
        )}
      </div>
    </LinkCardShell>
  );
}

function WorkflowPackageLinkedCard(
  props: Readonly<{
    instance: TFeatureInstanceLite;
    projectId?: string;
    cardType: "bid-package" | "design-package";
    highlighted?: boolean;
    highlightRole?: "driving" | "governing";
    isSuccessor?: boolean;
  }>
) {
  const {
    instance,
    projectId,
    cardType,
    highlighted,
    highlightRole,
    isSuccessor = false
  } = props;
  const { title, number } = getCardCommonProps(instance);
  const wfi = instance.feature_workflow_instance;
  const risk = wfi?.risk_level ?? undefined;
  const completed = isWorkflowCompletedStatus(wfi?.workflow_status);
  const hasScheduleAnchor = hasPlanOrActualOnLastMilestone(instance);
  const riskPillNode = buildHeaderRiskBadge(risk, completed, hasScheduleAnchor);
  const detailsPath = linkedFeatureDetailsPath(projectId, instance);
  const linkedMilestoneDisplay = instance.linked_to_milestone_name || "-";
  const showLinkedMilestoneRow =
    cardType !== "bid-package" || Boolean(instance.linked_to_milestone_name);

  return (
    <LinkCardShell
      cardType={cardType}
      highlighted={highlighted}
      highlightRole={highlightRole}
      cardStyle={
        showLinkedMilestoneRow
          ? {
              height: "auto",
              minHeight: LINK_CARD_HEIGHT_PX,
              paddingTop: 4,
              paddingBottom: 4
            }
          : undefined
      }
    >
      <div
        className="flex h-full w-full min-w-0 flex-col justify-center gap-0.5"
        style={linkCardHorizontalPaddingStyle}
      >
        <CardTitleRow
          title={title}
          subTitle={number}
          titleLinkTo={detailsPath}
          hideSubTitle
          extra={
            <div className="flex shrink-0 items-center justify-center gap-1.5">
              {riskPillNode}
            </div>
          }
        />
        {showLinkedMilestoneRow ? (
          <WorkflowPackageLinkedMilestoneRow
            milestoneName={linkedMilestoneDisplay}
            isSuccessor={isSuccessor}
          />
        ) : null}
      </div>
    </LinkCardShell>
  );
}

function ActivityLinkedCard(
  props: Readonly<{
    instance: TFeatureInstanceLite;
    highlighted?: boolean;
    highlightRole?: "driving" | "governing";
    isSuccessor?: boolean;
  }>
) {
  const { instance, highlighted, highlightRole, isSuccessor } = props;
  const { title, number } = getCardCommonProps(instance);
  const activity = instance.feature_activities?.[0];
  const datesTooltip = buildActivityLinkCardTooltip(instance);

  return (
    <LinkCardShell
      cardType="activity"
      highlighted={highlighted}
      highlightRole={highlightRole}
      datesTooltip={datesTooltip}
      isSuccessor={isSuccessor}
      tooltipWidthPx={getActivityLinkCardTooltipWidthPx(instance)}
    >
      <div
        className={linkCardBodyClassName}
        style={linkCardHorizontalPaddingStyle}
      >
        <CardTitleRow
          title={title}
          subTitle={number}
          extra={activity?.critical ? <CriticalActivityIndicator /> : null}
          hideSubTitle={false}
          disableTitleTooltip
        />
      </div>
    </LinkCardShell>
  );
}

function LinkedItemCard(
  props: Readonly<{
    instance: TFeatureInstanceLite | undefined;
    projectId?: string;
    linkParentEntity?: TLinkParentEntity;
    highlighted?: boolean;
    highlightRole?: "driving" | "governing";
    isSuccessor?: boolean;
    disableDatesTooltip?: boolean;
  }>
) {
  const {
    instance,
    projectId,
    linkParentEntity,
    highlighted,
    highlightRole,
    isSuccessor = false,
    disableDatesTooltip
  } = props;
  // Every caller maps over already-filtered (defined) instances, so the null
  // guard here is unreachable defensive code.
  /* v8 ignore next */
  if (!instance) return null;

  const featureTypeId = instance.feature_type_id;
  const cardHighlightProps = { highlighted, highlightRole };

  if (featureTypeId === FeatureTypes.MATERIALS) {
    return (
      <MaterialLinkedCard
        instance={instance}
        projectId={projectId}
        linkParentEntity={linkParentEntity}
        disableDatesTooltip={disableDatesTooltip}
        isSuccessor={isSuccessor}
        {...cardHighlightProps}
      />
    );
  }
  if (featureTypeId === FeatureTypes.ACTIVITIES) {
    return (
      <ActivityLinkedCard
        instance={instance}
        isSuccessor={isSuccessor}
        {...cardHighlightProps}
      />
    );
  }
  if (featureTypeId === FeatureTypes.BID_PACKAGE) {
    return (
      <WorkflowPackageLinkedCard
        instance={instance}
        projectId={projectId}
        cardType="bid-package"
        isSuccessor={isSuccessor}
        {...cardHighlightProps}
      />
    );
  }
  if (featureTypeId === FeatureTypes.DESIGN_PACKAGE) {
    return (
      <WorkflowPackageLinkedCard
        instance={instance}
        projectId={projectId}
        cardType="design-package"
        isSuccessor={isSuccessor}
        {...cardHighlightProps}
      />
    );
  }
  return (
    <SubmittalLinkedCard
      instance={instance}
      projectId={projectId}
      disableDatesTooltip={disableDatesTooltip}
      isSuccessor={isSuccessor}
      {...cardHighlightProps}
    />
  );
}

type SuccessorShiftMilestone = DateblockMilestoneLike & {
  sequence_no?: number;
};

const FEATURE_GROUP_LABELS: Partial<Record<FeatureTypes, string>> = {
  [FeatureTypes.SUBMITTALS]: "Submittals",
  [FeatureTypes.MATERIALS]: "Materials",
  [FeatureTypes.ACTIVITIES]: "Activities",
  [FeatureTypes.DESIGN_PACKAGE]: "Design Packages",
  [FeatureTypes.BID_PACKAGE]: "Bid Packages"
};

const LINKS_GROUP_CONTAINER_BG = "#0000000A";
const LINKS_GROUP_ACCORDION_ICON_COLOR = "#4448C1";
const LINKS_GROUP_ACCORDION_ICON_SIZE_PX = 14;

const linksGroupAccordionIconStyle: React.CSSProperties = {
  fontSize: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  width: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  height: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  color: LINKS_GROUP_ACCORDION_ICON_COLOR
};

type TLinksColumnGroup = {
  featureTypeId: number;
  label: string;
  instances: TFeatureInstanceLite[];
  emptyElement?: React.ReactNode;
};

/**
 * When a real (non-implicit) material is linked, hide system-generated
 * implicit material cards — same rule as Links card / classic dateblock.
 */
function filterImplicitMaterialsWhenRealExists(
  instances: TFeatureInstanceLite[]
): TFeatureInstanceLite[] {
  const materialInstances = instances.filter(
    (i) => i.feature_type_id === FeatureTypes.MATERIALS
  );
  if (materialInstances.length <= 1) return instances;
  if (!materialInstances.some((i) => !i.implicit)) return instances;
  return instances.filter(
    (i) => i.feature_type_id !== FeatureTypes.MATERIALS || !i.implicit
  );
}

function partitionInstancesByFeatureTypeOrder(
  instances: TFeatureInstanceLite[],
  order: readonly FeatureTypes[]
): TLinksColumnGroup[] {
  const buckets = new Map<FeatureTypes, TFeatureInstanceLite[]>();
  const orderedSet = new Set(order);
  const other: TFeatureInstanceLite[] = [];

  for (const inst of instances) {
    const ft = inst.feature_type_id;
    if (ft !== undefined && orderedSet.has(ft as FeatureTypes)) {
      const bucket = buckets.get(ft) ?? [];
      bucket.push(inst);
      buckets.set(ft, bucket);
    } else {
      other.push(inst);
    }
  }

  const groups: TLinksColumnGroup[] = [];
  for (const ft of order) {
    const list = buckets.get(ft);
    if (list?.length) {
      groups.push({
        featureTypeId: ft,
        // `ft` comes from a featureGroupOrder whose entries are all present in
        // FEATURE_GROUP_LABELS, so the `?? "Other"` fallback is unreachable.
        /* v8 ignore next */
        label: FEATURE_GROUP_LABELS[ft as FeatureTypes] ?? "Other",
        instances: list
      });
    }
  }
  if (other.length > 0) {
    groups.push({
      featureTypeId: -1,
      label: "Other",
      instances: other
    });
  }
  return groups;
}

function NoMaterialSuccessorsLink(
  props: Readonly<{
    projectId?: string;
    materialId?: string;
    canEditLinkingPage?: boolean;
  }>
) {
  const { projectId, materialId, canEditLinkingPage = true } = props;
  if (!projectId || !materialId) return null;

  const linkingPath = `/project/${projectId}/schedule/material-linking?&materialId=${materialId}`;

  return (
    <div
      className="flex items-center justify-between rounded border border-solid border-ciq-black-a10 bg-white p-2 text-xs"
      data-testid="no-material-successors-linked"
    >
      <span className="text-ciq-black-a80 font-semibold text-xs">
        No Successors Linked
      </span>
      {canEditLinkingPage ? (
        <Link
          to={linkingPath}
          className="border-0 bg-transparent p-0 text-xs leading-5 text-ciq-blue-600 hover:text-ciq-blue-600"
          data-testid="no-material-successors-link-button"
        >
          Link
        </Link>
      ) : null}
    </div>
  );
}

function buildLinksColumnGroups(
  instances: TFeatureInstanceLite[],
  order: readonly FeatureTypes[],
  options?: Readonly<{ submittalGroupEmptyElement?: React.ReactNode }>
): TLinksColumnGroup[] {
  const partitioned = partitionInstancesByFeatureTypeOrder(instances, order);
  const submittalEmpty = options?.submittalGroupEmptyElement;
  if (!submittalEmpty) return partitioned;

  const hasSubmittalLinked = instances.some(
    (i) => i.feature_type_id === FeatureTypes.SUBMITTALS
  );
  if (hasSubmittalLinked) return partitioned;

  const submittalPlaceholder: TLinksColumnGroup = {
    featureTypeId: FeatureTypes.SUBMITTALS,
    // SUBMITTALS is always present in FEATURE_GROUP_LABELS -> `?? "Submittal"` dead.
    /* v8 ignore next */
    label: FEATURE_GROUP_LABELS[FeatureTypes.SUBMITTALS] ?? "Submittal",
    instances: [],
    emptyElement: submittalEmpty
  };

  const byFeatureType = new Map(
    partitioned.map((group) => [group.featureTypeId, group])
  );
  const groups: TLinksColumnGroup[] = [];
  for (const ft of order) {
    if (ft === FeatureTypes.SUBMITTALS) {
      groups.push(byFeatureType.get(ft) ?? submittalPlaceholder);
    } else {
      const group = byFeatureType.get(ft);
      if (group) groups.push(group);
    }
  }
  const otherGroup = partitioned.find((g) => g.featureTypeId === -1);
  if (otherGroup) groups.push(otherGroup);
  return groups;
}

function LinksColumnGroupAccordion(
  props: Readonly<{
    group: TLinksColumnGroup;
    projectId?: string;
    linkParentEntity?: TLinkParentEntity;
    columnAlign: "left" | "right";
    governingSource?: TGoverningSource;
    projectingEntityId?: string;
    isExpanded: boolean;
    onAccordionClick: () => void;
  }>
) {
  const {
    group,
    projectId,
    linkParentEntity,
    columnAlign,
    governingSource,
    projectingEntityId,
    isExpanded,
    onAccordionClick
  } = props;
  const highlightRole = columnAlign === "left" ? "driving" : "governing";
  const instances = group.instances;
  const hasVisibleBody = instances.length > 0 || Boolean(group.emptyElement);

  return (
    <div
      className="min-w-0 overflow-hidden rounded-lg"
      style={{ backgroundColor: LINKS_GROUP_CONTAINER_BG }}
      data-testid={`links-column-group-${group.featureTypeId}`}
    >
      <div
        className="flex w-full items-center justify-between select-none"
        style={{
          paddingLeft: LINKS_GROUP_TITLE_LEFT_PADDING_PX,
          paddingRight: LINK_CARD_HORIZONTAL_MARGIN_PX,
          paddingTop: LINKS_GROUP_HEADER_VERTICAL_PADDING_PX,
          paddingBottom: LINKS_GROUP_HEADER_VERTICAL_PADDING_PX
        }}
        data-testid="links-column-group-header"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <FeatureTypeIconBadge
            featureTypeId={group.featureTypeId}
            size="md"
            testId="links-column-group-icon"
          />
          <span
            className="truncate text-xs font-semibold text-ciq-gray-800"
            data-testid="links-column-group-label"
          >
            {group.label}
          </span>
          <LinksGroupCountBadge count={group.instances.length} />
        </div>
        <button
          type="button"
          className="border-0 bg-transparent cursor-pointer p-0 leading-none"
          onClick={onAccordionClick}
          aria-expanded={isExpanded}
          data-testid="links-column-group-accordion"
        >
          {isExpanded ? (
            <UpOutlined style={linksGroupAccordionIconStyle} />
          ) : (
            <DownOutlined style={linksGroupAccordionIconStyle} />
          )}
        </button>
      </div>
      {isExpanded && hasVisibleBody ? (
        <div
          className="min-w-0 space-y-2"
          style={{
            ...linksGroupCardInsetStyle,
            paddingBottom: LINK_CARD_HORIZONTAL_MARGIN_PX
          }}
          data-testid="links-column-group-content"
        >
          {instances.map((inst) => (
            <LinkedItemCard
              key={`${inst.id ?? inst.title}-${inst.feature_type_id ?? ""}`}
              instance={inst}
              projectId={projectId}
              linkParentEntity={linkParentEntity}
              highlighted={isHighlightedLinkCard(
                inst,
                columnAlign,
                governingSource,
                projectingEntityId
              )}
              highlightRole={
                isHighlightedLinkCard(
                  inst,
                  columnAlign,
                  governingSource,
                  projectingEntityId
                )
                  ? highlightRole
                  : undefined
              }
              isSuccessor={columnAlign === "right"}
            />
          ))}
          {instances.length === 0 && group.emptyElement ? (
            <div
              className="w-full min-w-0 shrink-0"
              data-testid="links-column-group-empty"
            >
              {group.emptyElement}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function toggleGroupId(prev: Set<number>, groupId: number) {
  const next = new Set(prev);
  if (next.has(groupId)) {
    next.delete(groupId);
  } else {
    next.add(groupId);
  }
  return next;
}

function resolveDefaultExpandedGroupId(orderedGroups: TLinksColumnGroup[]) {
  const topGroup = orderedGroups[0];
  if (
    !topGroup ||
    (topGroup.instances.length === 0 && !topGroup.emptyElement)
  ) {
    return undefined;
  }
  return topGroup.featureTypeId;
}

function linksColumnPadding(
  align: "left" | "right",
  successorAxisWidth: number,
  successorCardLeftPaddingReductionPx: number
) {
  if (align === "right") {
    return {
      paddingLeft: successorAxisWidth - successorCardLeftPaddingReductionPx,
      paddingRight: LINKS_COLUMN_OUTER_PADDING_PX
    };
  }

  return {
    paddingLeft: LINKS_COLUMN_OUTER_PADDING_PX,
    paddingRight: LINKS_COLUMN_INNER_GUTTER_PX
  };
}

function LinksColumn(
  props: Readonly<{
    title: string;
    instances: Array<TFeatureInstanceLite | undefined>;
    align: "left" | "right";
    style?: React.CSSProperties;
    projectId?: string;
    /** Feature types in display order (Submittal → Material → Activity). */
    featureTypeGroupOrder?: readonly FeatureTypes[];
    getInstanceSortRawDate?: (instance: TFeatureInstanceLite) => string | null;
    instanceSortOrder?: "asc" | "desc";
    /**
     * Shown under the Submittal group when no submittal predecessor is linked
     * (e.g. `NoSubmittalOrPlannedSubmittalLink` on Material entities).
     */
    submittalGroupEmptyElement?: React.ReactNode;
    /** Shown when the column has no linked instances (material detail successors). */
    columnEmptyElement?: React.ReactNode;
    expandAllGroupsByDefault?: boolean;
    linkParentEntity?: TLinkParentEntity;
    governingSource?: TGoverningSource;
    /** Focal entity calculation_details.projecting_entity.id for driving highlight. */
    projectingEntityId?: string;
    /** Wider axis region for submittal focal-entity material successors. */
    successorAxisWidthPx?: number;
    /** Reduces successor card left padding (submittal detail only). */
    successorCardLeftPaddingReductionPx?: number;
    /** Material detail Focused View: shift successor cross further right. */
    materialFocusedLayout?: boolean;
    "data-testid"?: string;
  }>
) {
  const {
    title,
    instances,
    align,
    style,
    projectId,
    featureTypeGroupOrder,
    getInstanceSortRawDate,
    instanceSortOrder = "desc",
    submittalGroupEmptyElement,
    columnEmptyElement,
    expandAllGroupsByDefault = true,
    linkParentEntity,
    governingSource,
    projectingEntityId,
    successorAxisWidthPx,
    successorCardLeftPaddingReductionPx = 0,
    materialFocusedLayout = false
  } = props;
  const highlightRole = align === "left" ? "driving" : "governing";
  const testId = props["data-testid"];
  const filtered = instances.filter((i): i is TFeatureInstanceLite =>
    Boolean(i)
  );
  const groups = featureTypeGroupOrder
    ? buildLinksColumnGroups(filtered, featureTypeGroupOrder, {
        submittalGroupEmptyElement
      })
    : // featureTypeGroupOrder is always supplied by both call sites -> null arm dead.
      /* v8 ignore next */
      null;
  const visibleGroups = useMemo(
    // `groups` is non-null here (a featureTypeGroupOrder is always supplied), so
    // the optional-chain short-circuit and `?? []` fallback are unreachable.
    /* v8 ignore next */
    () => groups?.filter((g) => g.instances.length > 0 || g.emptyElement) ?? [],
    [groups]
  );
  const orderedGroups = useMemo(() => {
    // getInstanceSortRawDate is always supplied by both call sites -> guard dead.
    /* v8 ignore next */
    if (!getInstanceSortRawDate) return visibleGroups;
    return visibleGroups.map((group) => ({
      ...group,
      instances: stableSortByDate(
        group.instances,
        getInstanceSortRawDate,
        instanceSortOrder
      )
    }));
  }, [visibleGroups, getInstanceSortRawDate, instanceSortOrder]);
  const defaultExpandedGroupId = useMemo(
    () => resolveDefaultExpandedGroupId(orderedGroups),
    [orderedGroups]
  );
  const visibleGroupIdsKey = orderedGroups
    .map((g) => g.featureTypeId)
    .join(",");
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<number>>(
    () => new Set()
  );

  useLayoutEffect(() => {
    // expandAllGroupsByDefault defaults to true and neither call site overrides
    // it, so the false (else) arm is unreachable.
    /* v8 ignore next */
    if (expandAllGroupsByDefault) {
      const ids = visibleGroupIdsKey
        ? visibleGroupIdsKey.split(",").map((id) => Number(id))
        : [];
      setExpandedGroupIds(new Set(ids));
      return;
    }

    // Both call sites use the default expandAllGroupsByDefault=true, so the
    // single-group default-expansion branch below is unreachable.
    /* v8 ignore start */
    setExpandedGroupIds(
      defaultExpandedGroupId !== undefined
        ? new Set([defaultExpandedGroupId])
        : new Set()
    );
    /* v8 ignore stop */
  }, [visibleGroupIdsKey, defaultExpandedGroupId, expandAllGroupsByDefault]);

  const toggleGroup = (groupId: number) => {
    setExpandedGroupIds((prev) => toggleGroupId(prev, groupId));
  };

  const hasGroupedContent = visibleGroups.length > 0;

  const successorAxisWidth =
    align === "right"
      ? // renderSuccessorsColumn always supplies successorAxisWidthPx for the
        // right column, so the `?? DEFAULT` fallback is unreachable.
        /* v8 ignore next */
        (successorAxisWidthPx ?? LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX)
      : LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX;

  const labelRow =
    align === "left" ? (
      <PredecessorAxisLabelRow
        title={title}
        count={filtered.length > 1 ? filtered.length : undefined}
      />
    ) : (
      <SuccessorAxisLabelRow
        title={title}
        count={filtered.length > 1 ? filtered.length : undefined}
        successorAxisWidthPx={successorAxisWidth}
        focusedLayout={
          materialFocusedLayout ? { materialFocusedLayout: true } : undefined
        }
      />
    );

  const columnWidth =
    align === "right"
      ? computeLinksColumnSuccessorWidth(successorAxisWidth)
      : LINKS_COLUMN_PREDECESSOR_WIDTH;

  return (
    <div
      className="shrink-0 flex flex-col pt-8 self-start"
      style={{ width: columnWidth, ...style }}
      data-testid={testId}
    >
      <div className="h-10 shrink-0" />
      {labelRow}
      <div
        className="min-w-0 py-2 space-y-2"
        style={linksColumnPadding(
          align,
          successorAxisWidth,
          successorCardLeftPaddingReductionPx
        )}
      >
        {groups && hasGroupedContent ? (
          <div className="space-y-2">
            {orderedGroups.map((group) => (
              <LinksColumnGroupAccordion
                key={group.featureTypeId}
                group={group}
                projectId={projectId}
                linkParentEntity={linkParentEntity}
                columnAlign={align}
                governingSource={governingSource}
                projectingEntityId={projectingEntityId}
                isExpanded={expandedGroupIds.has(group.featureTypeId)}
                onAccordionClick={() => toggleGroup(group.featureTypeId)}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="w-full min-w-0 shrink-0"
            data-testid="links-column-empty-slot"
          >
            {columnEmptyElement ?? <div />}
          </div>
        ) : (
          // Unreachable: LinksColumn always receives a featureTypeGroupOrder, so
          // `groups` is non-null and any non-empty `filtered` buckets into a
          // visible group — the ungrouped fallback map never runs.
          /* v8 ignore start */
          filtered.map((inst) => (
            <LinkedItemCard
              key={`${inst.id ?? inst.title}-${inst.feature_type_id ?? ""}`}
              instance={inst}
              projectId={projectId}
              linkParentEntity={linkParentEntity}
              highlighted={isHighlightedLinkCard(
                inst,
                align,
                governingSource,
                projectingEntityId
              )}
              highlightRole={
                isHighlightedLinkCard(
                  inst,
                  align,
                  governingSource,
                  projectingEntityId
                )
                  ? highlightRole
                  : undefined
              }
              isSuccessor={align === "right"}
            />
          ))
          /* v8 ignore stop */
        )}
      </div>
    </div>
  );
}

function EntityDateblockNewInterface(
  props: Readonly<{
    entity: TEntityDateblock | undefined;
    governingSource?: TGoverningSource;
    afterChange?: () => void;
    permissions: TDateBlockPermissions;
    setApiCallLoading?: (isLoading: boolean) => void;
    entityHeaders?: any;
  }>
) {
  const {
    entity,
    governingSource,
    afterChange,
    permissions,
    setApiCallLoading,
    entityHeaders
  } = props;

  // useParams always returns an object inside a Router, so the `?? {}` is dead.
  /* v8 ignore next */
  const { projectId } = (useParams() as { projectId?: string }) ?? {};

  const layoutConfig = useMemo(
    () => getEntityDateblockLayoutConfig(entity),
    [entity]
  );

  const projectingEntityId = useMemo(
    () => getProjectingEntityId(entity),
    [entity]
  );

  const { predecessors, successors } = useMemo(() => {
    const linksAsTarget = (entity as any)?.links_as_target as
      | Array<TLinkLite>
      | undefined;
    const linksAsSource = (entity as any)?.links_as_source as
      | Array<TLinkLite>
      | undefined;
    /**
     * Filter out planning-only submittal predecessors — they're surfaced
     * separately via the `NoSubmittalOrPlannedSubmittalLink` empty card
     * (Submittal Plan section) and shouldn't appear as a regular linked
     * predecessor instance.
     */
    const isPlanningOnlySubmittal = (i?: TFeatureInstanceLite) =>
      i?.feature_type_id === FeatureTypes.PLANNING_ONLY_SUBMITTAL;

    const predecessorsRaw =
      linksAsTarget
        ?.map((l) =>
          attachLinkContextToInstance(
            l,
            l.source_feature_instance,
            entity as TLinkParentEntity
          )
        )
        .filter(
          (i): i is TFeatureInstanceLite =>
            Boolean(i) && !isPlanningOnlySubmittal(i)
        ) ?? [];

    const successorsRaw =
      linksAsSource
        ?.map((l) =>
          attachLinkContextToInstance(
            l,
            l.target_feature_instance,
            entity as TLinkParentEntity
          )
        )
        .filter((i): i is TFeatureInstanceLite => Boolean(i)) ?? [];

    return {
      predecessors: filterImplicitMaterialsWhenRealExists(predecessorsRaw),
      successors: filterImplicitMaterialsWhenRealExists(successorsRaw)
    };
  }, [entity]);

  const bidPackageLinkedMaterials = useMemo(
    () => getBidPackageLinkedMaterials(entity),
    [entity]
  );

  const bidPackageLinkedSubmittals = useMemo(
    () => getBidPackageLinkedSubmittals(entity),
    [entity]
  );

  const submittalGroupEmptyElement =
    layoutConfig.showSubmittalGroupEmptyElement ? (
      <NoSubmittalOrPlannedSubmittalLink
        compact
        canEditLinkingPage
        projectId={projectId}
        materialEntity={entity}
        afterChange={afterChange}
      />
    ) : undefined;

  /**
   * Pull the successor column LEFT into the dateblock's trailing empty zone
   * so the cross sits one short connector-line away from the last offset /
   * float rect, instead of being stranded after a large empty gap.
   */
  const showFloat = (entity as any)?.feature?.show_float ?? true;

  const successorShiftPx = useMemo(
    () =>
      computeSuccessorShiftPx(
        (entity as any)?.workflow_instance_milestones as
          | ReadonlyArray<SuccessorShiftMilestone>
          | undefined,
        {
          showFloat,
          materialFocusedLayout: layoutConfig.dateblock.materialFocusedLayout
        }
      ),
    [entity, showFloat, layoutConfig.dateblock.materialFocusedLayout]
  );

  const packageFeatureId = String(
    (entity as { id?: string | number } | undefined)?.id ??
      (entity as any)?.feature_workflow_instance?.feature_instance_id ??
      ""
  );

  const materialSuccessorsEmptyElement = layoutConfig.dateblock
    .materialFocusedLayout ? (
    <NoMaterialSuccessorsLink
      projectId={projectId}
      materialId={packageFeatureId || undefined}
      canEditLinkingPage={permissions.canEditDurations !== false}
    />
  ) : undefined;

  const successorsDisabled = !permissions.canEditDurations;

  if (!entity) return null;

  const successorShiftStyle =
    layoutConfig.successor.useGenericShift && successorShiftPx > 0
      ? { marginLeft: -successorShiftPx }
      : undefined;

  const bidSuccessorMarginStyle = layoutConfig.successor.useBidPackageMargin
    ? { marginLeft: computeBidPackageSuccessorMarginLeftPx(successorShiftPx) }
    : undefined;

  const entityMilestones =
    (entity as { workflow_instance_milestones?: TWorkflowMilestoneLite[] })
      .workflow_instance_milestones ?? [];
  const designPackageMilestones = entityMilestones
    .filter((m): m is TWorkflowMilestoneLite & { id: string; name: string } =>
      Boolean(m.id && m.name)
    )
    .map((m) => ({ id: m.id, name: m.name }));

  const renderPredecessorLinkCard = (inst: TFeatureInstanceLite) => (
    <LinkedItemCard
      key={`${inst.id ?? inst.title}-${inst.feature_type_id ?? ""}`}
      instance={inst}
      projectId={projectId}
      linkParentEntity={entity as TLinkParentEntity}
      highlighted={isHighlightedLinkCard(
        inst,
        "left",
        governingSource,
        projectingEntityId
      )}
      highlightRole={
        isHighlightedLinkCard(inst, "left", governingSource, projectingEntityId)
          ? "driving"
          : undefined
      }
    />
  );

  const renderSuccessorLinkCard = (inst: TFeatureInstanceLite) => (
    <LinkedItemCard
      key={`${inst.id ?? inst.title}-${inst.feature_type_id ?? ""}`}
      instance={inst}
      projectId={projectId}
      linkParentEntity={entity as TLinkParentEntity}
      highlighted={isHighlightedLinkCard(inst, "right", governingSource)}
      highlightRole={
        isHighlightedLinkCard(inst, "right", governingSource)
          ? "governing"
          : undefined
      }
      isSuccessor
    />
  );

  const renderBidPackageMaterialLinkCard = (
    inst: (typeof bidPackageLinkedMaterials)[number]
  ) => (
    <div key={inst.id} data-testid={`bid-linked-material-${inst.id}`}>
      <LinkedItemCard
        instance={inst as TFeatureInstanceLite}
        projectId={projectId}
        linkParentEntity={entity as TLinkParentEntity}
        isSuccessor
        disableDatesTooltip
      />
    </div>
  );

  const renderBidPackageSubmittalLinkCard = (
    inst: (typeof bidPackageLinkedSubmittals)[number]
  ) => (
    <div key={inst.id} data-testid={`bid-linked-submittal-${inst.id}`}>
      <LinkedItemCard
        instance={{
          ...(inst as TFeatureInstanceLite),
          feature_type_id: inst.feature_type_id ?? FeatureTypes.SUBMITTALS
        }}
        projectId={projectId}
        isSuccessor
        disableDatesTooltip
      />
    </div>
  );

  const renderPredecessorsColumn = () => {
    if (!layoutConfig.showPredecessors) return null;
    if (layoutConfig.predecessorColumn === "package-links") {
      return (
        <BidPackagePredecessorsColumn
          featureId={packageFeatureId}
          linkedCount={predecessors.length}
          disabled={successorsDisabled}
          afterChange={afterChange}
          linkCards={predecessors.map(renderPredecessorLinkCard)}
        />
      );
    }
    return (
      <LinksColumn
        title="Predecessors"
        instances={predecessors}
        align="left"
        projectId={projectId}
        featureTypeGroupOrder={layoutConfig.featureGroupOrder}
        getInstanceSortRawDate={getPredecessorSortRawDate}
        linkParentEntity={entity as TLinkParentEntity}
        governingSource={governingSource}
        projectingEntityId={projectingEntityId}
        submittalGroupEmptyElement={submittalGroupEmptyElement}
        data-testid="entity-dateblock-new-interface-predecessors"
      />
    );
  };

  const renderSuccessorsColumn = () => {
    if (layoutConfig.successorColumn === "bid-planner") {
      return (
        <BidPackageSuccessorsColumn
          featureId={packageFeatureId}
          entity={entity}
          materialLinkCards={bidPackageLinkedMaterials.map(
            renderBidPackageMaterialLinkCard
          )}
          submittalLinkCards={bidPackageLinkedSubmittals.map(
            renderBidPackageSubmittalLinkCard
          )}
          afterChange={afterChange}
          disabled={successorsDisabled}
          style={bidSuccessorMarginStyle}
          renderActivityCardTooltip={(card, link, instance) => {
            const activityInstance = attachLinkContextToInstance(
              link as TLinkLite,
              instance as TFeatureInstanceLite,
              entity as TLinkParentEntity
            ) as TFeatureInstanceLite;
            return (
              <LinkCardDatesTooltip
                content={buildActivityLinkCardTooltip(activityInstance)}
                isSuccessor
                widthPx={getActivityLinkCardTooltipWidthPx(activityInstance)}
              >
                {card}
              </LinkCardDatesTooltip>
            );
          }}
        />
      );
    }
    if (layoutConfig.successorColumn === "package-links") {
      return (
        <DesignPackageSuccessorsColumn
          featureId={packageFeatureId}
          linkedCount={successors.length}
          disabled={successorsDisabled}
          afterChange={afterChange}
          style={successorShiftStyle}
          milestones={designPackageMilestones}
          linkCards={successors.map(renderSuccessorLinkCard)}
        />
      );
    }
    return (
      <LinksColumn
        title="Successors"
        instances={successors}
        align="right"
        projectId={projectId}
        featureTypeGroupOrder={layoutConfig.featureGroupOrder}
        getInstanceSortRawDate={getSuccessorSortRawDate}
        instanceSortOrder="asc"
        linkParentEntity={entity as TLinkParentEntity}
        governingSource={governingSource}
        style={successorShiftStyle}
        successorAxisWidthPx={layoutConfig.successor.axisWidthPx}
        successorCardLeftPaddingReductionPx={
          layoutConfig.successor.cardLeftPaddingReductionPx || undefined
        }
        materialFocusedLayout={
          layoutConfig.successor.focusedCrossShift.materialFocusedLayout
        }
        columnEmptyElement={materialSuccessorsEmptyElement}
        data-testid="entity-dateblock-new-interface-successors"
      />
    );
  };

  return (
    <DateblockThreeColumnLayout
      showPredecessors={layoutConfig.showPredecessors}
      predecessors={renderPredecessorsColumn()}
      dateblock={
        <EntityDateblock
          entity={entity}
          governingSource={governingSource}
          afterChange={afterChange}
          permissions={permissions}
          setApiCallLoading={setApiCallLoading}
          entityHeaders={entityHeaders}
          materialFocusedLayout={layoutConfig.dateblock.materialFocusedLayout}
          designPackageFocusedLayout={
            layoutConfig.dateblock.designPackageFocusedLayout
          }
          hideGoverningTask
        />
      }
      successors={renderSuccessorsColumn()}
    />
  );
}

export default EntityDateblockNewInterface;
